import { useParams } from "react-router-dom";
import Breadcrumb from "@/components/base/Breadcrumb";
import FilmCard from "@/components/feature/FilmCard";
import StarCard from "@/components/feature/StarCard";
import ShareButtons from "@/components/feature/ShareButtons";
import SectionHeading from "@/components/feature/SectionHeading";
import ContentMissing from "@/components/feature/ContentMissing";
import { getFilmById, getFilmCast, getRelatedFilms } from "@/lib/content";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";

export default function FilmDetail() {
  const { id } = useParams<{ id: string }>();
  const film = id ? getFilmById(id) : undefined;

  useDocumentMeta({
    title: film ? `${film.title} - 巨乳娱乐资讯网` : "影视作品 - 巨乳娱乐资讯网",
    description: film
      ? `${film.title}影视作品介绍：${film.summary} 导演${film.director}，${film.year}年上映。`
      : "影视作品详情页，聚合影片介绍、演职人员与相关推荐。",
    keywords: film
      ? `${film.title},${film.genres.join(",")},${film.director},影视介绍,巨乳娱乐资讯网`
      : "影视作品,影视介绍,巨乳娱乐资讯网",
  });

  if (!film) {
    return <ContentMissing message="该影视作品可能已被移除，或链接有误。" />;
  }

  const cast = getFilmCast(film);
  const related = getRelatedFilms(film);

  return (
    <div className="mx-auto w-full max-w-7xl px-4 py-6 md:px-8 md:py-8">
      <Breadcrumb
        items={[
          { label: "首页", to: "/" },
          { label: "影视作品", to: "/category/films" },
          { label: film.title },
        ]}
      />

      <section className="mt-4 grid grid-cols-1 gap-6 md:grid-cols-[260px_1fr]">
        <div className="w-full overflow-hidden rounded-lg bg-background-100 md:w-[260px]">
          <div className="aspect-[2/3] w-full">
            <img
              src={film.cover}
              alt={`${film.title} 影视海报`}
              title={`${film.title} 影视作品 巨乳娱乐资讯网`}
              className="h-full w-full object-cover object-top"
            />
          </div>
        </div>

        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            <div className="flex flex-wrap items-center gap-3">
              <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
                {film.title}
              </h1>
              <span className="flex items-center gap-1 rounded-md bg-accent-500 px-2 py-1 text-sm font-medium text-background-50">
                <i className="ri-star-fill text-xs" aria-hidden="true"></i>
                {film.rating.toFixed(1)}
              </span>
            </div>
            <div className="flex flex-wrap items-center gap-2 text-xs text-foreground-500 md:text-sm">
              <span>{film.year}</span>
              <span className="text-foreground-300">·</span>
              <span>{film.region}</span>
              <span className="text-foreground-300">·</span>
              <span>{film.genres.join(" / ")}</span>
              <span className="text-foreground-300">·</span>
              <span>热度 {film.heat.toLocaleString()}</span>
            </div>
          </div>

          <dl className="grid grid-cols-1 gap-2 rounded-lg border border-background-200 bg-background-50 p-4 text-sm md:grid-cols-2">
            <div className="flex gap-2">
              <dt className="shrink-0 text-foreground-500">导演：</dt>
              <dd className="text-foreground-800">{film.director}</dd>
            </div>
            <div className="flex gap-2">
              <dt className="shrink-0 text-foreground-500">主演：</dt>
              <dd className="text-foreground-800">
                {cast.map((star) => star.name).join("、") || "暂无"}
              </dd>
            </div>
            <div className="flex gap-2">
              <dt className="shrink-0 text-foreground-500">更新时间：</dt>
              <dd className="text-foreground-800">{film.updatedAt}</dd>
            </div>
            <div className="flex gap-2">
              <dt className="shrink-0 text-foreground-500">标签：</dt>
              <dd className="flex flex-wrap gap-1">
                {film.tags.map((tag) => (
                  <span
                    key={tag}
                    className="rounded-full bg-secondary-100 px-2 py-0.5 text-xs text-secondary-900"
                  >
                    {tag}
                  </span>
                ))}
              </dd>
            </div>
          </dl>

          <ShareButtons title={`${film.title} - 巨乳娱乐资讯网`} />
        </div>
      </section>

      <section className="mt-10">
        <h2 className="mb-3 flex items-center gap-2 font-heading text-lg font-bold text-foreground-950 md:text-xl">
          <span className="inline-block h-5 w-1 rounded-full bg-accent-500" aria-hidden="true"></span>
          简介与剧情概述
        </h2>
        <div className="flex flex-col gap-3 rounded-lg border border-background-200 bg-background-50 p-5 text-sm leading-relaxed text-foreground-700 md:text-base">
          <p className="font-medium text-foreground-900">{film.summary}</p>
          {film.synopsis.map((paragraph) => (
            <p key={paragraph.slice(0, 12)}>{paragraph}</p>
          ))}
        </div>
      </section>

      {cast.length > 0 && (
        <section className="mt-10">
          <SectionHeading
            title="主要演员"
            subtitle="点击卡片查看人物完整资料"
          />
          <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4">
            {cast.map((star) => (
              <StarCard key={star.id} star={star} />
            ))}
          </div>
        </section>
      )}

      {related.length > 0 && (
        <section className="mt-10">
          <SectionHeading
            title="相关影视推荐"
            subtitle="相似类型或同一导演的作品"
            moreTo="/category/films"
          />
          <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
            {related.map((item) => (
              <FilmCard key={item.id} film={item} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}